import { useCallback, useRef } from 'react';
import { Dialog as DialogType, DialogSection, DialogVariable, dialogStore } from '@/lib/stores/dialogStore';
import { createLogicFlow, updateLogicFlow } from '@/lib/stores/logicFlowStore';
import { markStringsAsDeprecated } from '@/lib/stores/stringsStore';
import { Node, Edge } from '@xyflow/react';
import { DialogData, ViewMode, SpeakerMode } from '../types';
import { stripHtml, ensureSectionIds, migrateTextToDialog, convertNodesToSections, migrateDialogSectionsToNodes } from '../utils/dialogUtils';

export interface UseDialogSubmitProps {
  // State values
  dialogName: string;
  dialogKey: string;
  description: string;
  translatable: boolean;
  startingSection: string | undefined;
  speakerMode: SpeakerMode;
  speakerTypeId: string | null;
  speakerTemplateIds: string[];
  dialogVariables: DialogVariable[];
  tags: string[];
  status: string;
  owner: string | null;
  editPermissions: 'Open' | 'Locked';
  viewMode: ViewMode;
  flowInitialized: boolean;
  flowId: string | null;
  flowNodes: Node[];
  flowEdges: Edge[];
  needsFlowCreation: boolean;
  effectiveGameId: string;
  initialData?: DialogData | null;
  inline: boolean;
  
  // Refs
  sectionsRef: React.MutableRefObject<DialogSection[]>;
  localFlowSectionsRef: React.MutableRefObject<DialogSection[]>;
  regenerateFlowFromSectionsRef: React.MutableRefObject<(() => void) | undefined>;
  skipDraftSaveRef?: React.MutableRefObject<boolean>;
  savedStateSnapshotRef?: React.MutableRefObject<any>;
  deletedStringIdsRef?: React.MutableRefObject<Set<string>>;
  
  // Draft management
  clearDraft?: () => void;
  
  // Setters
  setDialogName: (name: string) => void;
  setDialogKey: (key: string) => void;
  setDescription: (desc: string) => void;
  setTranslatable: (val: boolean) => void;
  setStartingSection: (section: string | undefined) => void;
  setSpeakerMode: (mode: SpeakerMode) => void;
  setSpeakerTypeId: (id: string | null) => void;
  setSpeakerTemplateIds: (ids: string[]) => void;
  setSections: React.Dispatch<React.SetStateAction<DialogSection[]>>;
  setLocalFlowSections: React.Dispatch<React.SetStateAction<DialogSection[]>>;
  setFlowNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setFlowEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  setFlowId: (id: string | null) => void;
  setFlowInitialized: (val: boolean) => void;
  setNeedsFlowCreation: (val: boolean) => void;
  setIsSubmitting: (val: boolean) => void;
  setHasUnsavedChanges: (val: boolean) => void;
  
  // Callbacks
  onSave?: (dialogData: DialogData) => Promise<DialogType | void>;
  onUpdate?: (dialogData: DialogData) => Promise<DialogType | void>;
  onClose: () => void;
  toast: (options: { title: string; description: string; variant?: 'default' | 'destructive' }) => void;
}

export interface DialogSubmitHandlers {
  handleSubmit: () => Promise<void>;
  handleCancel: () => void;
}

/**
 * Custom hook for dialog submit/save logic
 */
export function useDialogSubmit({
  dialogName,
  dialogKey,
  description,
  translatable,
  startingSection,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  tags,
  status,
  owner,
  editPermissions,
  viewMode,
  flowInitialized,
  flowId,
  flowNodes,
  flowEdges,
  needsFlowCreation,
  effectiveGameId,
  initialData,
  inline,
  sectionsRef,
  localFlowSectionsRef,
  regenerateFlowFromSectionsRef,
  skipDraftSaveRef,
  savedStateSnapshotRef,
  deletedStringIdsRef,
  clearDraft,
  setDialogName,
  setDialogKey,
  setDescription,
  setTranslatable,
  setStartingSection,
  setSpeakerMode,
  setSpeakerTypeId,
  setSpeakerTemplateIds,
  setSections,
  setLocalFlowSections,
  setFlowNodes,
  setFlowEdges,
  setFlowId,
  setFlowInitialized,
  setNeedsFlowCreation,
  setIsSubmitting,
  setHasUnsavedChanges,
  onSave,
  onUpdate,
  onClose,
  toast,
}: UseDialogSubmitProps): DialogSubmitHandlers {
  
  const handleSubmit = useCallback(async () => {
    console.log('[useDialogSubmit] handleSubmit called');
    
    // Use refs to get the LATEST sections state
    const currentLocalFlowSections = localFlowSectionsRef.current;
    const currentSections = sectionsRef.current;
    
    // Determine which sections to save
    const sectionsToSave = (viewMode === 'flow' && flowInitialized && currentLocalFlowSections.length > 0) 
      ? currentLocalFlowSections 
      : currentSections;
    
    console.log('[useDialogSubmit] Sections selection for save:', {
      viewMode,
      flowInitialized,
      localFlowSectionsCount: currentLocalFlowSections.length,
      regularSectionsCount: currentSections.length,
      selectedSectionsCount: sectionsToSave.length,
      usingLocalFlow: viewMode === 'flow' && flowInitialized && currentLocalFlowSections.length > 0,
    });
    
    // Log connection details for debugging
    const connectionDetails = sectionsToSave.flatMap((section) => 
      (section.fields || []).flatMap((field) => 
        (field.options || []).filter(opt => opt.next_section || opt.next_dialog).map((opt) => ({
          section: section.name,
          field: field.text?.substring(0, 30),
          option: opt.option_text?.substring(0, 30),
          next_section: opt.next_section,
          next_dialog: opt.next_dialog,
          next_dialog_section: opt.next_dialog_section
        }))
      )
    );
    
    console.log('[useDialogSubmit] Sections to save:', {
      viewMode,
      usingLocalFlow: viewMode === 'flow' && flowInitialized && currentLocalFlowSections.length > 0,
      sectionsCount: sectionsToSave.length,
      connectionsCount: connectionDetails.length,
      connections: connectionDetails,
      sectionNames: sectionsToSave.map(s => s.name),
      fullSectionsData: JSON.stringify(sectionsToSave, null, 2)
    });
    
    // Commit local flow changes to main sections state for consistency
    if (viewMode === 'flow' && flowInitialized && currentLocalFlowSections.length > 0) {
      console.log('[useDialogSubmit] Committing local flow changes to main state');
      setSections(JSON.parse(JSON.stringify(currentLocalFlowSections)));
      sectionsRef.current = currentLocalFlowSections;
      // Note: Keep flowInitialized as true to maintain flow state after save
      // This ensures edit mode can properly sync when switching views
    }
    
    // Validation
    if (!dialogName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Dialog name is required',
        variant: 'destructive',
      });
      return;
    }

    if (!dialogKey.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Dialog key is required',
        variant: 'destructive',
      });
      return;
    }

    if (sectionsToSave.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'At least one section is required',
        variant: 'destructive',
      });
      return;
    }

    for (const section of sectionsToSave) {
      if (!section.name?.trim()) {
        toast({
          title: 'Validation Error',
          description: 'All sections must have a name',
          variant: 'destructive',
        });
        return;
      }
      
      if (!section.fields || section.fields.length === 0) {
        toast({
          title: 'Validation Error',
          description: `Section "${section.name}" must have at least one field`,
          variant: 'destructive',
        });
        return;
      }
      
      for (let i = 0; i < section.fields.length; i++) {
        const field = section.fields[i];
        // Allow the first field to be empty, but require text for all other fields
        if (i > 0 && !stripHtml(field.text).trim()) {
          toast({
            title: 'Validation Error',
            description: `All fields in section "${section.name}" must have text (except the first field)`,
            variant: 'destructive',
          });
          return;
        }
      }
    }

    console.log('[useDialogSubmit] Validation passed, starting save...');
    setIsSubmitting(true);

    try {
      let currentFlowId = flowId;
      
      console.log('[useDialogSubmit] Flow state before save:', {
        flowId,
        needsFlowCreation,
        hasFlowNodes: flowNodes.length > 0,
        flowNodesCount: flowNodes.length,
        currentFlowId
      });
      
      // Create or update flow if needed
      if (needsFlowCreation && flowNodes.length > 0) {
        console.log('[useDialogSubmit] Creating new flow in logic_flows...');
        try {
          const newFlow = await createLogicFlow({
            game_id: effectiveGameId,
            name: `${dialogName} - Flow`,
            description: `Flow visualization for dialog: ${dialogName}`,
            data: {
              nodes: flowNodes as any,
              edges: flowEdges as any,
            },
            folder: initialData?.folder || null,
          });
          
          console.log('[useDialogSubmit] Flow created successfully:', {
            flowId: newFlow.id,
            flowName: newFlow.name
          });
          
          currentFlowId = newFlow.id;
          setFlowId(newFlow.id);
          setNeedsFlowCreation(false);
          
          if (initialData?.id) {
            const currentDialogs = dialogStore.getData();
            const updatedDialogs = currentDialogs.map(dialog => 
              dialog.id === initialData.id 
                ? { ...dialog, flow: newFlow.id }
                : dialog
            );
            dialogStore.setData(updatedDialogs);
          }
          
          toast({
            title: 'Flow Created',
            description: 'Flow visualization has been saved',
          });
        } catch (error) {
          console.error('[useDialogSubmit] Failed to create flow:', error);
          toast({
            title: 'Warning',
            description: 'Dialog saved but flow creation failed',
            variant: 'destructive',
          });
        }
      } else if (flowId && flowNodes.length > 0) {
        console.log('[useDialogSubmit] Updating existing flow:', flowId);
        try {
          await updateLogicFlow({
            id: flowId,
            game_id: effectiveGameId,
            name: `${dialogName} - Flow`,
            data: {
              nodes: flowNodes as any,
              edges: flowEdges as any,
            },
          });
          console.log('[useDialogSubmit] Flow updated successfully');
        } catch (error) {
          console.error('[useDialogSubmit] Failed to update flow:', error);
        }
      }
      
      // Build edge styles metadata
      const edgeStyles: { [edgeId: string]: { type?: string; animated?: boolean } } = {};
      flowEdges.forEach(edge => {
        // Only store if different from default values
        if (edge.type !== 'default' || edge.animated !== true) {
          edgeStyles[edge.id] = {
            type: edge.type,
            animated: edge.animated,
          };
        }
      });
      
      // Convert sections to nodes (nodes are the source of truth)
      const dataToConvert = {
        sections: sectionsToSave,
        translatable: translatable,
        startingSection: startingSection,
        speakerMode: speakerMode,
        speakerTypeId: speakerTypeId,
        speakerTemplateIds: speakerTemplateIds,
        variables: dialogVariables,
        flowMetadata: Object.keys(edgeStyles).length > 0 ? { edgeStyles } : undefined,
      };
      
      const convertedData = migrateDialogSectionsToNodes(dataToConvert);
      
      console.log('[useDialogSubmit] Converted sections to nodes for save:', {
        sectionsCount: sectionsToSave.length,
        nodesCount: convertedData.nodes?.length || 0,
        hasSetterNodes: convertedData.nodes?.some(n => n.type === 'setter') || false,
      });
      
      // Build dialog data - save nodes as primary, keep sections for backward compatibility
      const dialogData: DialogData = {
        ...(initialData?.id && { id: initialData.id }),
        game_id: effectiveGameId,
        name: dialogName,
        dialog_key: dialogKey,
        description: description || '',
        tags: tags,
        folder: initialData?.folder || null,
        status: status as 'draft' | 'published',
        owner: owner,
        edit_permissions: editPermissions,
        flow: currentFlowId,
        data: {
          nodes: convertedData.nodes,
          startingNode: convertedData.startingNode,
          sections: sectionsToSave, // Keep for backward compatibility
          translatable: translatable,
          startingSection: startingSection,
          speakerMode: speakerMode,
          speakerTypeId: speakerTypeId,
          speakerTemplateIds: speakerTemplateIds,
          variables: dialogVariables,
          flowMetadata: Object.keys(edgeStyles).length > 0 ? { edgeStyles } : undefined,
        },
      };
      
      console.log('[useDialogSubmit] Saving dialog data:', {
        dialogId: dialogData.id,
        dialogName: dialogData.name,
        flowId: dialogData.flow,
        hasFlow: !!dialogData.flow,
      });

      if (initialData?.id && onUpdate) {
        const updatedDialog = await onUpdate(dialogData);
        
        if (updatedDialog) {
          console.log('[useDialogSubmit] Dialog updated, received from server:', {
            dialogId: updatedDialog.id,
            dialogName: updatedDialog.name,
            flowId: updatedDialog.flow,
          });
          
          // Mark deleted strings as deprecated if dialog is translatable
          if (translatable && deletedStringIdsRef && deletedStringIdsRef.current.size > 0) {
            const stringIdsToDeprecate = Array.from(deletedStringIdsRef.current);
            console.log(`[useDialogSubmit] Marking ${stringIdsToDeprecate.length} deleted strings as deprecated`);
            
            try {
              await markStringsAsDeprecated(stringIdsToDeprecate, effectiveGameId, toast);
              console.log(`[useDialogSubmit] Successfully deprecated ${stringIdsToDeprecate.length} strings`);
              
              // Clear the set after successful deprecation
              deletedStringIdsRef.current.clear();
            } catch (error) {
              console.error('[useDialogSubmit] Failed to deprecate strings:', error);
              // Don't fail the save if string deprecation fails
              toast({
                title: 'Warning',
                description: 'Dialog saved but failed to mark some strings as deprecated',
                variant: 'destructive',
              });
            }
          }
          
          // Update local dialogStore
          const currentDialogs = dialogStore.getData();
          const dialogIndex = currentDialogs.findIndex(d => d.id === updatedDialog.id);
          
          if (dialogIndex !== -1) {
            const updatedDialogs = [...currentDialogs];
            updatedDialogs[dialogIndex] = updatedDialog;
            dialogStore.setData(updatedDialogs);
          } else {
            dialogStore.setData([...currentDialogs, updatedDialog]);
          }
          
          // Update local component state
          setDialogName(updatedDialog.name || 'New Dialog');
          setDialogKey(updatedDialog.dialog_key || '');
          setDescription(updatedDialog.description || '');
          setTranslatable(updatedDialog.data?.translatable !== false);
          setStartingSection(updatedDialog.data?.startingSection);
          setSpeakerMode(updatedDialog.data?.speakerMode || 'none');
          setSpeakerTypeId(updatedDialog.data?.speakerTypeId || null);
          setSpeakerTemplateIds(updatedDialog.data?.speakerTemplateIds || []);
          
          if (updatedDialog.flow) {
            setFlowId(updatedDialog.flow);
          }
          
          // Reload sections from server response - convert nodes to sections
          let freshSections: DialogSection[];
          
          if (updatedDialog.data?.nodes && updatedDialog.data.nodes.length > 0) {
            // Nodes exist - use them as source of truth
            console.log('[useDialogSubmit] Converting returned nodes to sections for UI');
            freshSections = convertNodesToSections(updatedDialog.data.nodes);
          } else {
            // Fallback to sections (shouldn't happen with new system)
            console.warn('[useDialogSubmit] No nodes in response, using sections (legacy)');
            freshSections = ensureSectionIds(updatedDialog.data?.sections || []);
            freshSections = migrateTextToDialog(freshSections);
          }
          
          console.log('[useDialogSubmit] Comparing saved vs returned sections:', {
            savedSectionsCount: sectionsToSave.length,
            savedSectionNames: sectionsToSave.map(s => s.name),
            freshSectionsCount: freshSections.length,
            freshSectionNames: freshSections.map(s => s.name),
            sectionsMatch: sectionsToSave.length === freshSections.length,
          });
          
          // Log detailed section comparison
          if (sectionsToSave.length !== freshSections.length) {
            console.warn('[useDialogSubmit] ⚠️ MISMATCH: Server returned different number of sections!', {
              sent: sectionsToSave.map(s => ({ name: s.name, fields: s.fields?.length || 0 })),
              received: freshSections.map(s => ({ name: s.name, fields: s.fields?.length || 0 }))
            });
          }
          
          // Keep local flow structure but merge string_ids from server response.
          // Without this, the next save would create new translation strings
          // because the local state never received string_ids from the backend.
          if (viewMode === 'flow' && flowInitialized && currentLocalFlowSections.length > 0) {
            // Build map of field id → string_id from server response
            const serverStringIdMap = new Map<string, string>();
            // Build map of (field id, option index) → option string_id from server response
            const serverOptionStringIdMap = new Map<string, Map<number, string>>();
            for (const section of freshSections) {
              if (section.fields) {
                for (const field of section.fields) {
                  if (field.string_id) {
                    serverStringIdMap.set(field.id, field.string_id);
                  }
                  // Collect option-level string_ids
                  if (field.options) {
                    const optMap = new Map<number, string>();
                    field.options.forEach((opt, idx) => {
                      if (opt.string_id) {
                        optMap.set(idx, opt.string_id);
                      }
                    });
                    if (optMap.size > 0) {
                      serverOptionStringIdMap.set(field.id, optMap);
                    }
                  }
                }
              }
            }
            
            // Merge string_ids into the local flow sections
            const mergedFlowSections = currentLocalFlowSections.map(section => ({
              ...section,
              fields: section.fields?.map(field => {
                let mergedField = field;
                // Merge field-level string_id
                const serverId = serverStringIdMap.get(field.id);
                if (serverId && !field.string_id) {
                  mergedField = { ...mergedField, string_id: serverId };
                }
                // Merge option-level string_ids
                const optMap = serverOptionStringIdMap.get(field.id);
                if (optMap && mergedField.options) {
                  const mergedOptions = mergedField.options.map((opt, idx) => {
                    const optStringId = optMap.get(idx);
                    if (optStringId && !opt.string_id) {
                      return { ...opt, string_id: optStringId };
                    }
                    return opt;
                  });
                  mergedField = { ...mergedField, options: mergedOptions };
                }
                return mergedField;
              }) || [],
            }));
            
            setSections(mergedFlowSections);
            sectionsRef.current = mergedFlowSections;
            localFlowSectionsRef.current = mergedFlowSections;
            setLocalFlowSections(mergedFlowSections);
            console.log('[useDialogSubmit] Merged string_ids into localFlowSections after save (flow mode)');
          } else {
            // Not in flow mode: use server response for both sections and localFlowSections
            setSections(freshSections);
            sectionsRef.current = freshSections;
            localFlowSectionsRef.current = freshSections;
            setLocalFlowSections(freshSections);
            console.log('[useDialogSubmit] Using server response for sections (not in flow mode)');
          }
          
          // Don't regenerate flow after save - keep existing nodes with their positions
          // The flow nodes already have the correct positions and were just saved
          // Regeneration should only happen when switching views or editing structure
        }
        
        // Set flag to prevent useEffects from saving new drafts
        if (skipDraftSaveRef) {
          skipDraftSaveRef.current = true;
        }
        
        // Clear draft immediately to prevent stale draft from being restored on reload
        if (clearDraft) {
          console.log('[useDialogSubmit] Clearing draft after successful update');
          clearDraft();
        }
        
        // CRITICAL FIX: Update saved state snapshot to prevent false change detection
        // The snapshot becomes the new baseline for detecting future changes
        const sectionsToSnapshot = viewMode === 'flow' && flowInitialized 
          ? currentLocalFlowSections 
          : currentSections;
        
        if (savedStateSnapshotRef) {
          savedStateSnapshotRef.current = {
            ...initialData,
            name: dialogName,
            dialog_key: dialogKey,
            description: description,
            data: {
              ...initialData?.data,
              sections: sectionsToSnapshot,
              translatable: translatable,
              speakerMode: speakerMode,
              speakerTypeId: speakerTypeId,
              speakerTemplateIds: speakerTemplateIds,
              variables: dialogVariables,
            },
          };
        }
        
        // Use setTimeout to ensure all state updates have settled before clearing unsaved changes
        // This prevents race conditions where state updates trigger hasUnsavedChanges(true) after save
        setTimeout(() => {
          setHasUnsavedChanges(false);
        }, 0);
        
        if (!inline) {
          onClose();
        }
      } else if (onSave) {
        const newDialog = await onSave(dialogData);
        
        if (newDialog) {
          console.log('[useDialogSubmit] New dialog created, received from server:', {
            dialogId: newDialog.id,
            dialogName: newDialog.name,
            flowId: newDialog.flow,
          });
          
          // Mark deleted strings as deprecated if dialog is translatable
          // (This can happen if user creates a dialog, adds strings, then deletes them before first save)
          if (translatable && deletedStringIdsRef && deletedStringIdsRef.current.size > 0) {
            const stringIdsToDeprecate = Array.from(deletedStringIdsRef.current);
            console.log(`[useDialogSubmit] Marking ${stringIdsToDeprecate.length} deleted strings as deprecated`);
            
            try {
              await markStringsAsDeprecated(stringIdsToDeprecate, effectiveGameId, toast);
              console.log(`[useDialogSubmit] Successfully deprecated ${stringIdsToDeprecate.length} strings`);
              
              // Clear the set after successful deprecation
              deletedStringIdsRef.current.clear();
            } catch (error) {
              console.error('[useDialogSubmit] Failed to deprecate strings:', error);
              // Don't fail the save if string deprecation fails
              toast({
                title: 'Warning',
                description: 'Dialog saved but failed to mark some strings as deprecated',
                variant: 'destructive',
              });
            }
          }
          
          // Update local dialogStore
          const currentDialogs = dialogStore.getData();
          const existingIndex = currentDialogs.findIndex(d => d.id === newDialog.id);
          
          if (existingIndex !== -1) {
            const updatedDialogs = [...currentDialogs];
            updatedDialogs[existingIndex] = newDialog;
            dialogStore.setData(updatedDialogs);
          } else {
            dialogStore.setData([...currentDialogs, newDialog]);
          }
          
          // Update local component state
          setDialogName(newDialog.name || 'New Dialog');
          setDialogKey(newDialog.dialog_key || '');
          setDescription(newDialog.description || '');
          setTranslatable(newDialog.data?.translatable !== false);
          setStartingSection(newDialog.data?.startingSection);
          setSpeakerMode(newDialog.data?.speakerMode || 'none');
          setSpeakerTypeId(newDialog.data?.speakerTypeId || null);
          setSpeakerTemplateIds(newDialog.data?.speakerTemplateIds || []);
          
          if (newDialog.flow) {
            setFlowId(newDialog.flow);
          }
          
          // Reload sections from server response - convert nodes to sections
          let freshSections: DialogSection[];
          
          if (newDialog.data?.nodes && newDialog.data.nodes.length > 0) {
            // Nodes exist - use them as source of truth
            console.log('[useDialogSubmit] Converting returned nodes to sections for UI');
            freshSections = convertNodesToSections(newDialog.data.nodes);
          } else {
            // Fallback to sections (shouldn't happen with new system)
            console.warn('[useDialogSubmit] No nodes in response, using sections (legacy)');
            freshSections = ensureSectionIds(newDialog.data?.sections || []);
            freshSections = migrateTextToDialog(freshSections);
          }
          
          // Keep local flow structure but merge string_ids from server response
          if (viewMode === 'flow' && flowInitialized && currentLocalFlowSections.length > 0) {
            const serverStringIdMap = new Map<string, string>();
            const serverOptionStringIdMap = new Map<string, Map<number, string>>();
            for (const section of freshSections) {
              if (section.fields) {
                for (const field of section.fields) {
                  if (field.string_id) {
                    serverStringIdMap.set(field.id, field.string_id);
                  }
                  // Collect option-level string_ids
                  if (field.options) {
                    const optMap = new Map<number, string>();
                    field.options.forEach((opt, idx) => {
                      if (opt.string_id) {
                        optMap.set(idx, opt.string_id);
                      }
                    });
                    if (optMap.size > 0) {
                      serverOptionStringIdMap.set(field.id, optMap);
                    }
                  }
                }
              }
            }
            
            const mergedFlowSections = currentLocalFlowSections.map(section => ({
              ...section,
              fields: section.fields?.map(field => {
                let mergedField = field;
                // Merge field-level string_id
                const serverId = serverStringIdMap.get(field.id);
                if (serverId && !field.string_id) {
                  mergedField = { ...mergedField, string_id: serverId };
                }
                // Merge option-level string_ids
                const optMap = serverOptionStringIdMap.get(field.id);
                if (optMap && mergedField.options) {
                  const mergedOptions = mergedField.options.map((opt, idx) => {
                    const optStringId = optMap.get(idx);
                    if (optStringId && !opt.string_id) {
                      return { ...opt, string_id: optStringId };
                    }
                    return opt;
                  });
                  mergedField = { ...mergedField, options: mergedOptions };
                }
                return mergedField;
              }) || [],
            }));
            
            setSections(mergedFlowSections);
            sectionsRef.current = mergedFlowSections;
            localFlowSectionsRef.current = mergedFlowSections;
            setLocalFlowSections(mergedFlowSections);
            console.log('[useDialogSubmit] Merged string_ids into localFlowSections after create (flow mode)');
          } else {
            // Not in flow mode: use server response for both sections and localFlowSections
            setSections(freshSections);
            sectionsRef.current = freshSections;
            localFlowSectionsRef.current = freshSections;
            setLocalFlowSections(freshSections);
            console.log('[useDialogSubmit] Using server response for sections (not in flow mode)');
          }
          
          // Don't regenerate flow after save - keep existing nodes with their positions
          // The flow nodes already have the correct positions and were just saved
          // Regeneration should only happen when switching views or editing structure
        }
        
        // CRITICAL FIX: Update saved state snapshot to prevent false change detection
        // The snapshot becomes the new baseline for detecting future changes
        const sectionsToSnapshot = viewMode === 'flow' && flowInitialized 
          ? currentLocalFlowSections 
          : currentSections;
        
        if (savedStateSnapshotRef) {
          savedStateSnapshotRef.current = {
            id: newDialog.id,
            game_id: effectiveGameId,
            name: dialogName,
            dialog_key: dialogKey,
            description: description,
            data: {
              sections: sectionsToSnapshot,
              translatable: translatable,
              speakerMode: speakerMode,
              speakerTypeId: speakerTypeId,
              speakerTemplateIds: speakerTemplateIds,
              variables: dialogVariables,
            },
          };
        }
        
        // Set flag to prevent useEffects from saving new drafts
        if (skipDraftSaveRef) {
          skipDraftSaveRef.current = true;
        }
        
        // Clear draft immediately to prevent stale draft from being restored on reload
        if (clearDraft) {
          console.log('[useDialogSubmit] Clearing draft after successful create');
          clearDraft();
        }
        
        // Use setTimeout to ensure all state updates have settled before clearing unsaved changes
        // This prevents race conditions where state updates trigger hasUnsavedChanges(true) after save
        setTimeout(() => {
          setHasUnsavedChanges(false);
        }, 0);
        
        if (!inline) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error saving dialog:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    dialogName, dialogKey, description, translatable, startingSection, speakerMode, speakerTypeId, 
    speakerTemplateIds, dialogVariables, tags, status, owner, editPermissions,
    viewMode, flowInitialized, flowId, 
    flowNodes, flowEdges, needsFlowCreation, effectiveGameId, initialData, inline,
    sectionsRef, localFlowSectionsRef, regenerateFlowFromSectionsRef, skipDraftSaveRef, deletedStringIdsRef, clearDraft, setDialogName, setDialogKey, setDescription,
    setTranslatable, setSpeakerMode, setSpeakerTypeId, setSpeakerTemplateIds, setStartingSection,
    setSections, setLocalFlowSections, setFlowNodes, setFlowEdges, setFlowId, setFlowInitialized, setNeedsFlowCreation, setIsSubmitting,
    setHasUnsavedChanges, onSave, onUpdate, onClose, toast
  ]);

  const handleCancel = useCallback(() => {
    // If in flow view with unsaved changes, discard local flow state
    if (viewMode === 'flow' && flowInitialized) {
      console.log('[useDialogSubmit] Discarding local flow changes');
      setFlowInitialized(false);
    }
    
    onClose();
  }, [viewMode, flowInitialized, setFlowInitialized, onClose]);

  return {
    handleSubmit,
    handleCancel,
  };
}

